import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Plus,
  MapPin,
  Calendar,
  Users,
  FileText,
  ClipboardCheck,
  TrendingUp,
  Search,
  Settings,
  Eye,
  Edit,
  Trash2,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Image,
  Upload,
  ChevronRight,
  Loader2,
  Shield,
  Lock,
  UserPlus,
  UserMinus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status, t }) => {
  const statusConfig = {
    active: { color: "bg-green-500/20 text-green-400 border-green-500/30", label: t ? t('common.active') : "Active" },
    completed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: t ? t('common.completed') : "Completed" },
    on_hold: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: t ? t('projects.onHold') : "On Hold" },
    cancelled: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: t ? t('projects.cancelled') : "Cancelled" }
  };
  
  const config = statusConfig[status] || statusConfig.active;
  
  return (
    <Badge className={`${config.color} border`}>
      {config.label}
    </Badge>
  );
};

// Project Card Component
const ProjectCard = ({ project, onClick }) => {
  return (
    <Card 
      className="bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/50 transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`local-project-card-${project.project_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white group-hover:text-[#23F7DD] transition-colors font-['Unbounded']">
              {project.title}
            </h3>
            <div className="flex items-center gap-2 mt-1 text-sm text-[#94A3B8]">
              <MapPin className="w-4 h-4" />
              <span>{project.location_city}</span>
            </div>
          </div>
          <StatusBadge status={project.status} />
        </div>
        
        {project.description && (
          <p className="text-sm text-[#94A3B8] mb-4 line-clamp-2">{project.description}</p>
        )}
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-[#23F7DD]">
              <TrendingUp className="w-4 h-4" />
              <span className="font-bold">{project.progress_count || 0}</span>
            </div>
            <p className="text-xs text-[#64748B] mt-1">Progress</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-purple-400">
              <ClipboardCheck className="w-4 h-4" />
              <span className="font-bold">{project.control_reports_count || 0}</span>
            </div>
            <p className="text-xs text-[#64748B] mt-1">Reports</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-blue-400">
              <FileText className="w-4 h-4" />
              <span className="font-bold">{project.documents_count || 0}</span>
            </div>
            <p className="text-xs text-[#64748B] mt-1">Docs</p>
          </div>
        </div>
        
        {/* Dates */}
        {(project.start_date || project.end_date) && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 text-xs text-[#64748B]">
            {project.start_date && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>Start: {new Date(project.start_date).toLocaleDateString()}</span>
              </div>
            )}
            {project.end_date && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>End: {new Date(project.end_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Create Project Dialog
const CreateProjectDialog = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location_address: "",
    location_city: "",
    project_type: "assembly",
    start_date: "",
    end_date: "",
    required_crew_size: 1,
    complexity: "medium",
    estimated_duration_hours: "",
    safety_notes: "",
    scope_details: "",
    budget_min: "",
    budget_max: "",
    contact_email: "",
    contact_phone: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.location_address || !formData.location_city) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : null,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        required_crew_size: parseInt(formData.required_crew_size)
      };
      
      await axios.post(`${API}/local-projects`, payload);
      toast.success("Project created successfully!");
      onOpenChange(false);
      onCreated();
      setFormData({
        title: "",
        description: "",
        location_address: "",
        location_city: "",
        project_type: "assembly",
        start_date: "",
        end_date: "",
        required_crew_size: 1,
        complexity: "medium",
        estimated_duration_hours: "",
        safety_notes: "",
        scope_details: "",
        budget_min: "",
        budget_max: "",
        contact_email: "",
        contact_phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#23F7DD]" />
            Create Local Project
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#94A3B8]">Project Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Oslo Office Building"
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                required
              />
            </div>
            
            <div className="md:col-span-2 space-y-2">
              <Label className="text-[#94A3B8]">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Project description..."
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD] min-h-[80px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Address *</Label>
              <Input
                value={formData.location_address}
                onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                placeholder="Street address"
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">City *</Label>
              <Input
                value={formData.location_city}
                onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                placeholder="City"
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Start Date</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">End Date</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Project Type</Label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
              >
                <option value="assembly">Assembly</option>
                <option value="disassembly">Disassembly</option>
                <option value="modification">Modification</option>
                <option value="inspection">Inspection</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Complexity</Label>
              <select
                value={formData.complexity}
                onChange={(e) => setFormData({ ...formData, complexity: e.target.value })}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Crew Size</Label>
              <Input
                type="number"
                min="1"
                value={formData.required_crew_size}
                onChange={(e) => setFormData({ ...formData, required_crew_size: e.target.value })}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Estimated Hours</Label>
              <Input
                type="number"
                value={formData.estimated_duration_hours}
                onChange={(e) => setFormData({ ...formData, estimated_duration_hours: e.target.value })}
                placeholder="e.g., 40"
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Access Management Dialog
const AccessManagementDialog = ({ open, onOpenChange }) => {
  const [users, setUsers] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    if (open) {
      fetchAccessList();
      fetchAllUsers();
    }
  }, [open]);

  const fetchAccessList = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/access/users`);
      setAccessList(res.data);
    } catch (error) {
      console.error("Error fetching access list:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      const users = res.data.users || res.data;
      setAllUsers(users.filter(u => u.role !== "platform_admin"));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const grantAccess = async (userId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/local-projects/access/grant-tab?user_id=${userId}`);
      toast.success("Access granted!");
      fetchAccessList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant access");
    } finally {
      setLoading(false);
    }
  };

  const revokeAccess = async (userId) => {
    if (!confirm("Are you sure you want to revoke this user's access?")) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/local-projects/access/revoke-tab/${userId}`);
      toast.success("Access revoked");
      fetchAccessList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke access");
    } finally {
      setLoading(false);
    }
  };

  const accessUserIds = new Set(accessList.map(a => a.user_id));
  const filteredUsers = allUsers.filter(u => 
    !accessUserIds.has(u.user_id) &&
    (u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
     u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#23F7DD]" />
            Manage Access
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Current Access */}
          <div>
            <h4 className="text-sm font-medium text-[#94A3B8] mb-3">Users with Access ({accessList.length})</h4>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {accessList.map((access) => (
                <div key={access.user_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{access.user?.name || "Unknown"}</p>
                    <p className="text-xs text-[#64748B]">{access.user?.email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revokeAccess(access.user_id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <UserMinus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {accessList.length === 0 && (
                <p className="text-sm text-[#64748B] text-center py-4">No users have access yet</p>
              )}
            </div>
          </div>
          
          {/* Add Users */}
          <div>
            <h4 className="text-sm font-medium text-[#94A3B8] mb-3">Grant Access to User</h4>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users by name or email..."
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD] mb-3"
            />
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {filteredUsers.slice(0, 10).map((user) => (
                <div key={user.user_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{user.name}</p>
                    <p className="text-xs text-[#64748B]">{user.email} • {user.role?.replace(/_/g, ' ')}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => grantAccess(user.user_id)}
                    disabled={loading}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {filteredUsers.length === 0 && searchTerm && (
                <p className="text-sm text-[#64748B] text-center py-4">No users found</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function LocalProjects() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/access/check`);
      setHasAccess(res.data.has_access);
      if (res.data.has_access) {
        fetchProjects();
      }
    } catch (error) {
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/local-projects${params}`);
      setProjects(res.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error(t('errors.generic'));
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchProjects();
    }
  }, [statusFilter, hasAccess]);

  const filteredProjects = projects.filter(p =>
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.location_city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAdmin = user?.role === "platform_admin";

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title={t('localProjects.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-10 h-10 text-[#23F7DD] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!hasAccess) {
    return (
      <DashboardLayout navItems={adminNavItems} title={t('localProjects.title')}>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{t('errors.unauthorized')}</h2>
          <p className="text-[#94A3B8] max-w-md">
            {t('errors.forbidden')}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title={t('localProjects.title')}>
      <div className="space-y-6" data-testid="local-projects-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Unbounded']">{t('localProjects.title')}</h1>
            <p className="text-[#94A3B8]">{t('common.description')}</p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setAccessDialogOpen(true)}
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {t('roles.manageRoles')}
                </Button>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('localProjects.newProject')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#64748B] w-5 h-5" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('projects.searchProjects')}
              className="pl-10 bg-[#0A0A0A] border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>
          <div className="flex gap-2">
            {["all", "active", "completed", "on_hold"].map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status 
                  ? "bg-[#23F7DD] text-[#050505]" 
                  : "border-white/20 text-[#94A3B8] hover:text-white"
                }
              >
                {status === "all" ? t('common.all') : 
                 status === "active" ? t('common.active') : 
                 status === "completed" ? t('common.completed') : 
                 t('projects.onHold')}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Link key={project.project_id} to={`/local-projects/${project.project_id}`}>
                <ProjectCard project={project} t={t} />
              </Link>
            ))}
          </div>
        ) : (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-16 h-16 text-[#64748B] mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">{t('projects.noProjects')}</h3>
              <p className="text-[#94A3B8] text-center max-w-md">
                {searchTerm || statusFilter !== "all" 
                  ? t('common.noResults')
                  : t('localProjects.createProject')
                }
              </p>
              {isAdmin && !searchTerm && statusFilter === "all" && (
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="mt-4 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('localProjects.createProject')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dialogs */}
        <CreateProjectDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={fetchProjects}
        />
        
        <AccessManagementDialog
          open={accessDialogOpen}
          onOpenChange={setAccessDialogOpen}
        />
      </div>
    </DashboardLayout>
  );
}
